import { AssigneeType, PostStatusPropertyValue } from 'api/clients/PropertyClient'
import { Button } from 'blaze-ui/Button'
import { SocialChannelType } from 'common/ShareSheetV2/Screens/Default/Default'
import { useArticlePostPreviewContext } from 'components/ArticlePostPreview/ArticlePostPreviewContext'
import { usePostProperties } from 'components/ArticlePostPreview/usePostProperties'
import { ConnectAccountDrawer } from 'components/PostStatusDropdown/ConnectAccountDrawer'
import { PostOrScheduleDrawer } from './PostOrScheduleDrawer'
import { CalendarEdit, Send1 } from 'blaze-ui/icons/20'
import { PostStatusDropdownProps } from 'components/PostStatusDropdown/PostStatusDropdown'
import { usePostStatusDropdownState } from 'components/PostStatusDropdown/usePostStatusDropdownState'
import { UserIntent } from 'components/PostStatusDropdown/Types'
import Styles from './Actions.module.scss'
import { Text } from 'blaze-ui/Text/Text'
import dayjs from 'dayjs'
import { CrossPostDrawer } from '../CrossPostDrawer/CrossPostDrawer'
import ArrowSwitchHorizontal from 'blaze-ui/icons/20/ArrowSwitchHorizontal'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'
import { ButtonVariant } from 'blaze-ui/Button/Types'
import cx from 'classnames'

interface Props extends PostStatusDropdownProps {
  variant?: ButtonVariant
}

export const AutoPostScheduled = ({
  assignee,
  afterExecute,
  statusOverride,
  size,
  className,
  variant = 'tertiary',
  showUnschedule = false,
  isDisabled,
}: Pick<PostStatusDropdownProps, 'assignee' | 'afterExecute' | 'statusOverride' | 'size'> & {
  className?: string
  variant?: ButtonVariant
  showUnschedule?: boolean
  isDisabled?: boolean
}) => {
  const { almanacFile } = useArticlePostPreviewContext()
  const { drawersStore } = useRootStore()

  const { postAt } = usePostProperties({
    id: almanacFile.id,
    type: AssigneeType.Article,
  })

  const {
    executeAction,
    dispatch: calendarDispatch,
    state: calendarState,
  } = usePostStatusDropdownState({
    assignee,
    afterExecute,
    statusOverride,
    isMobile: true,
  })
  return (
    <div className={cx(Styles.scheduledTimeContainer, className)}>
      <div className={Styles.scheduledTime}>
        <span className={Styles.label}>Scheduled to post on</span>
        <Text>{dayjs(postAt).format('ddd, MMM D h:mma z')}</Text>
      </div>
      <Button
        variant={variant}
        frontIcon={CalendarEdit}
        isDisabled={isDisabled}
        size={size}
        onPress={() => {
          calendarDispatch({ type: UserIntent.ScheduleAutoPost })
          drawersStore.openDrawer(PostOrScheduleDrawer, {
            calendarState,
            calendarDispatch,
            executeAction,
            defaultToCalendar: true,
            showUnschedule,
          })
        }}
      >
        Edit
      </Button>
    </div>
  )
}

export const PostButton = observer(
  ({
    assignee,
    afterExecute,
    statusOverride,
    hasIntegrationForChannel,
    channel,
    disabledKeys,
    variant = 'primary',
    size = 'lg',
  }: Props) => {
    const {
      almanacFile,
      scheduledPost,
      state: { loading: postLoading },
    } = useArticlePostPreviewContext()
    const { drawersStore } = useRootStore()

    const { status } = usePostProperties({
      id: almanacFile.id,
      type: AssigneeType.Article,
    })

    const {
      executeAction,
      isLoading,
      dispatch: calendarDispatch,
      state: calendarState,
    } = usePostStatusDropdownState({
      assignee,
      afterExecute,
      statusOverride,
      isMobile: true,
    })

    const isDisabled = isLoading || postLoading || scheduledPost?.is_posting

    const button = () => {
      if (!hasIntegrationForChannel)
        return (
          <Button
            isDisabled={isDisabled}
            size={size}
            variant={variant}
            onPress={() => {
              drawersStore.openDrawer(ConnectAccountDrawer, { channel: channel as SocialChannelType })
            }}
          >
            Connect Your Account to Post
          </Button>
        )

      switch (status?.value) {
        case PostStatusPropertyValue.AutoPostScheduled:
          return (
            <Button
              isDisabled={isDisabled}
              size={size}
              variant={variant}
              onPress={() => executeAction(UserIntent.RemoveReminder)}
            >
              Unschedule
            </Button>
          )
        case PostStatusPropertyValue.Posted:
          return (
            <Button
              isDisabled={isDisabled}
              size={size}
              variant={variant}
              frontIcon={ArrowSwitchHorizontal}
              fullWidth
              onPress={() => drawersStore.openDrawer(CrossPostDrawer)}
            >
              Post to Another Platform
            </Button>
          )
        case PostStatusPropertyValue.Failed:
          return (
            <Button
              isDisabled={isDisabled}
              frontIcon={Send1}
              size={size}
              variant={variant}
              onPress={() => {
                drawersStore.openDrawer(PostOrScheduleDrawer, {
                  calendarState,
                  calendarDispatch,
                  executeAction,
                })
              }}
            >
              Retry
            </Button>
          )
        case PostStatusPropertyValue.Draft:
        default:
          return (
            <Button
              isDisabled={
                isLoading ||
                postLoading ||
                disabledKeys?.includes(UserIntent.PostNow) ||
                disabledKeys?.includes(UserIntent.ScheduleAutoPost)
              }
              frontIcon={Send1}
              size={size}
              variant={variant}
              onPress={() => {
                drawersStore.openDrawer(PostOrScheduleDrawer, {
                  calendarState,
                  calendarDispatch,
                  executeAction,
                })
              }}
            >
              Post or Schedule
            </Button>
          )
      }
    }

    return (
      <>
        {status?.value === PostStatusPropertyValue.AutoPostScheduled && (
          <AutoPostScheduled
            assignee={assignee}
            afterExecute={afterExecute}
            statusOverride={statusOverride}
            size={size}
            isDisabled={isDisabled}
          />
        )}
        {button()}
      </>
    )
  },
)
